import { BaseService } from './api'
import type { OrderbookQueryParams, OrderbookResponse, OrderbookItem } from '@/types/otc'

/**
 * The query parameters `GET /api/v1/orderbook/public` accepts.
 *
 * 🔴 The backend enforces an ALLOW-LIST and answers 400 for anything else — it
 * is not a filter that silently drops what it does not understand. The reason
 * is worth knowing before you extend this: the envelope's `total` is an oracle
 * for any predicate the caller can express, so a filter on a hidden financial
 * column would let two requests bracket a value and ten pin it down. That is
 * why `min_offered_amount` / `max_offered_amount` are absent here, and why
 * `sort_by` is narrower than `SortBy`.
 */
const PUBLIC_ALLOWED_SORT_FIELDS = ['DATE', 'PROJECT_NAME'] as const
type PublicSortBy = (typeof PUBLIC_ALLOWED_SORT_FIELDS)[number]

export interface PublicOrderbookQueryParams {
  search?: string
  type?: OrderbookQueryParams['type']
  deal_type?: OrderbookQueryParams['deal_type']
  industry?: string
  sort_by?: PublicSortBy
  sort_direction?: OrderbookQueryParams['sort_direction']
  limit?: number
  offset?: number
}

/** The endpoint's hard cap. A larger `limit` is a 400, not a clamp. */
const PUBLIC_MAX_LIMIT = 50

/**
 * Narrow a full orderbook query down to what the public endpoint accepts.
 *
 * Everything financial is DROPPED rather than translated: a tranche-size filter
 * has no non-financial equivalent, and sorting by `OFFERED_AMOUNT` is exactly
 * the ordering oracle the allow-list exists to prevent. Dropping them means a
 * gated user sees the unfiltered listing instead of a 400, which is the right
 * failure mode for a filter they should not have been offered in the first
 * place.
 */
export const toPublicOrderbookParams = (
  params?: OrderbookQueryParams,
): PublicOrderbookQueryParams => {
  if (!params) return {}
  const sortBy = params.sort_by
  const publicSortBy = sortBy === 'DATE' || sortBy === 'PROJECT_NAME' ? sortBy : undefined
  return {
    search: params.search,
    type: params.type,
    deal_type: params.deal_type,
    industry: params.industry,
    sort_by: publicSortBy,
    // `sort_direction` alone is meaningless to the endpoint and the backend
    // defaults it, so only send it alongside an accepted sort field.
    sort_direction: publicSortBy ? params.sort_direction : undefined,
    limit: params.limit === undefined ? undefined : Math.min(params.limit, PUBLIC_MAX_LIMIT),
    offset: params.offset,
  }
}

export class OTCService extends BaseService {
  async getOrderbook(params?: OrderbookQueryParams): Promise<OrderbookResponse> {
    try {
      const response = await this.api.get<OrderbookResponse>('/orderbook', { params })
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch orderbook')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  /**
   * The identity-only listing, for a session that has not completed
   * verification.
   *
   * 🔴 This is the gate, not a hint to the UI. The rows come back with no
   * `offered_amount` and no `offered_fully_diluted_value` because the server
   * never reads those columns for this route — a browser with devtools open
   * finds nothing to un-hide. Every row carries `financials_hidden: true`,
   * which is what the renderers key on.
   *
   * The endpoint requires authentication (401 otherwise) despite the name:
   * "public" means "carries no private data", not "open to anyone".
   */
  async getPublicOrderbook(params?: PublicOrderbookQueryParams): Promise<OrderbookResponse> {
    try {
      const response = await this.api.get<OrderbookResponse>('/orderbook/public', { params })
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch orderbook')
      }
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  async getHotDeals(): Promise<OrderbookItem[]> {
    try {
      const response = await this.getOrderbook({ limit: 5 })
      return response.data
    } catch (error) {
      this.handleError(error)
    }
  }

  async getIndustries(): Promise<string[]> {
    try {
      const response = await this.get<{ data: string[] }>('/orderbook/industries')
      return response.data || []
    } catch (error) {
      console.error('Failed to fetch industries:', error)
      return []
    }
  }
}

export const otcService = new OTCService() 